import React, { useState, useEffect } from "react";
import { Button, Col, Input, Row } from "antd";
import { AutoResizeTextArea } from "../../AutoResizeTextArea";
import styles from "./style.scss";
import ReactGA from "react-ga4";
import { useLocation as useRouterLocation } from "react-router-dom";

import { useSelector } from "react-redux";
import { selectAdData } from "../../../routes/AdForm/adForm.ducks";
import { useLocation, useMakes } from "../../../routes/Configuration/hooks";
import { findMake, findModel } from "../../../routes/Configuration/helpers";

import { CategoryName, CategoryNameHr, ModuleNameHr } from "../../../models";
import { fuels, realEstateType, landTypes } from "./helpers";

import { getWindowWidth, isSearchMobile, notification } from "../../../services";

import suggestTitleHelper, { SuggestTitleStateI } from "../../../utils/suggestTitleHelper";
import Levenshtein from "../../../utils/Levenshtein";


export interface SuggestedTitleProps {
  title: string;
  placeholder: string;
  isDisabled: boolean;
  size: "large" | "small" | "default";
  field: string;
  value: any;
  onChange: (value: any) => void;
}

const SuggestedTitle = (props: SuggestedTitleProps) => {
  const {
    title,
    placeholder,
    isDisabled,
    size,
    field,
    value
  } = props;

  const initialState: SuggestTitleStateI = {};

  const location = useRouterLocation();
  const selectedData = useSelector(selectAdData);

  const isVehicles = location.pathname.includes(ModuleNameHr.vehicles);
  const isCar = isVehicles && location.pathname.includes(CategoryNameHr.car);
  const isMotorbike = isVehicles && location.pathname.includes(CategoryNameHr.motorbike);

  // Makes of the category being edited (configuration/makes?category=)
  const carMakes = useMakes(isCar ? CategoryName.car : undefined);
  const motorbikeMakes = useMakes(isMotorbike ? CategoryName.motorbike : undefined);

  // Location names of the selected city / settlement (configuration/locations/:id)
  const selectedCity = useLocation(selectedData && selectedData.cityId);
  const selectedSettlement = useLocation(selectedData && selectedData.settlementId);

  const [state, setState] = useState(initialState);
  const [suggestedTitleValue, setSuggestedTitleValue] = useState("");
  const [disabled, setDisabled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [changed, setChanged] = useState(false);

  const isMobile = isSearchMobile(getWindowWidth());

  useEffect(() => {
    if(!selectedData || !selectedData.module || !selectedData.category) return;

    // if module is vehicles and category is car
    if (
      location.pathname.includes(ModuleNameHr.vehicles) && 
      location.pathname.includes(CategoryNameHr.car)
    ) {

      // set module to state and set other properties to empty string
      setState(prev => ({
        ...prev,
        template: "{makeName} {modelName}, {makeYear}. godište, {cubicCapacity} {fuel}",
        makeName: "",
        modelName: "",
        makeYear: "",
        fuel: "",
        cubicCapacity: "",
      }));

      // process form data
      const {
        makeId,
        modelId,
        makeYear,
        cubicCapacity,
        fuel
      } = selectedData;

      // process vehicle make and model
      if (carMakes && makeId && modelId) {
        const make = findMake(carMakes, makeId);
        const model = findModel(carMakes, modelId);
        if (make) {
          setState(prev => ({ ...prev, makeName: make.name }));
        }
        if (model) {
          setState(prev => ({ ...prev, modelName: model.name }));
        }
      }
      // process vehicle make year
      if (makeYear) {
        // makeYear is 2019-06-01T00:00:00Z so we need to extract only year
        setState(prev => ({ ...prev, makeYear: makeYear.substring(0, 4) }));
      }

      // process vehicle fuel 
      if (fuel) {
        fuels.forEach(selectedFuel => {
          if (selectedFuel.value == fuel) {
            setState(prev => ({ ...prev, fuel: selectedFuel.title }));
          }
        });
      }

      // process vehicle cubic capacity
      if (cubicCapacity) {
        setState(prev => ({
          ...prev,
          cubicCapacity: (cubicCapacity / 1000).toFixed(1),
        }));
      } 
    
    // if module is vehicles and category is motorbike
    } else if (
      location.pathname.includes(ModuleNameHr.vehicles) && 
      location.pathname.includes(CategoryNameHr.motorbike)
    ) {

      // set module to state and set other properties to empty string
      setState(prev => ({
        ...prev,
        template: "{makeName} {modelName}, {makeYear}. godište",
        makeName: "",
        modelName: "",
        makeYear: "",
      }));

      // process form data
      const {
        makeId,
        modelId,
        makeYear,
      } = selectedData;

      // process vehicle make and model
      if (motorbikeMakes && makeId && modelId) {
        const make = findMake(motorbikeMakes, makeId);
        const model = findModel(motorbikeMakes, modelId);
        if (make) {
          setState(prev => ({ ...prev, makeName: make.name }));
        }
        if (model) {
          setState(prev => ({ ...prev, modelName: model.name }));
        }
      }
      // process vehicle make year
      if (makeYear) {
        // makeYear is 2019-06-01T00:00:00Z so we need to extract only year
        setState(prev => ({ ...prev, makeYear: makeYear.substring(0, 4) }));
      }

      // if module is real estate 
      // and category is one of the following 
      // housesForSale, flatsForSale, housesForRent, flatsForRent
    } else if (
      location.pathname.includes(ModuleNameHr.realEstate) && 
      (
        location.pathname.includes(CategoryNameHr.housesForSale) ||
        location.pathname.includes(CategoryNameHr.flatsForSale) ||
        location.pathname.includes(CategoryNameHr.housesForRent) ||
        location.pathname.includes(CategoryNameHr.flatsForRent)
      )
    ) {

      // set module to state and set other properties to empty string
      setState(prev => ({
        ...prev,
        template: "{categoryName} {cityName} ({settlementName}), {area}m2, {numberOfRooms}",
        categoryName: "",
        numberOfRooms: "",
        area: 0,
        cityName: "",
        settlementName: "",
      }));

      // process form data
      const {
        category,
        settlementId,
        area: localArea,
        numberOfRooms: localNumberOfRooms
      } = selectedData;

      // process real estate category and number of rooms
      if (
        category &&
        localNumberOfRooms >= 0
      ) {
        realEstateType.forEach(type => {
          if (type.value == category || type.valueHr == category) {
            setState(prev => ({ ...prev, categoryName: type.title }));
            type.rooms.forEach(room => {
              if (room.value == localNumberOfRooms) {
                setState(prev => ({ ...prev, numberOfRooms: room.title }));
              }
            });
          }
        });
      }

      // process real estate area
      if (localArea) {
        setState(prev => ({ ...prev, area: localArea }));
      }

      // process real estate location
      if (settlementId && selectedCity && selectedSettlement) {
        setState(prev => ({
          ...prev,
          cityName: selectedCity.name,
          settlementName: selectedSettlement.name
        }));
      }

    // if module is real estate 
    // and category is one of the following 
    // landsForRent, landsForSale
    } else if (
      location.pathname.includes(ModuleNameHr.realEstate) &&
      (
        location.pathname.includes(CategoryNameHr.landsForRent) ||
        location.pathname.includes(CategoryNameHr.landsForSale)
      )
    ) {

      // set module to state and set other properties to empty string
      setState(prev => ({
        ...prev,
        template: "{landType} zemljište, {cityName} ({settlementName}), {area}m2",
        landType: "",
        cityName: "",
        settlementName: "",
        area: 0,
      }));

      // process form data
      const {
        landType,
        settlementId,
        area: localArea,
      } = selectedData;

      // process real estate land type
      if (landType) {
        landTypes.forEach(selectedLandType => {
          if (selectedLandType.value == landType) {
            setState(prev => ({ ...prev, landType: selectedLandType.title }));
          }
        });
      }

      // process real estate area
      if (localArea) {
        setState(prev => ({ ...prev, area: localArea }));
      }

      // process real estate location
      if (settlementId && selectedCity && selectedSettlement) {
        setState(prev => ({
          ...prev,
          cityName: selectedCity.name,
          settlementName: selectedSettlement.name
        }));
      }
    }
  }, [
    JSON.stringify(selectedData),
    carMakes,
    motorbikeMakes,
    selectedCity,
    selectedSettlement,
    JSON.stringify(location),
    JSON.stringify(state),
  ]);

  useEffect(() => {
    // check if all state object properties are set
    const values = Object.values(state);
    const isSet = values.every(v => v);

    // if all properties are set then we can suggest title
    const newSuggestedTitle = isSet ? suggestTitleHelper(state) : "";

    if (newSuggestedTitle) {
      const percentageSimilarity = Math.ceil(
        100 - (
          100 * Levenshtein(
            value.title,
            newSuggestedTitle
          )
        ) / Math.max(
          value.title.length,
          newSuggestedTitle.length
        )
      );

      if (percentageSimilarity < 80) {
        if (!changed) setDisabled(false);
        setHidden(false);
      } else {
        setDisabled(true);
        if (!changed) setHidden(true);
      }

      if (!changed) {
        setSuggestedTitleValue(newSuggestedTitle);
      }
    } else {
      setHidden(true);
    }
  }, [
    JSON.stringify(state),
    JSON.stringify(value),
    changed,
  ]);

  const handleChange = element => {
    const { value } = element.target;

    // validate title length
    if (value.length > 100) {
      notification.show("Naslov ne smije sadržavati više od 100 znakova!");
      return;
    }

    if (value.length == 0) {
      notification.show("Naslov ne smije biti prazan!");
      return;
    }

    // set disabled to false if it was disabled
    if (disabled) {
      setDisabled(false);
    }

    // set suggested title to value
    setSuggestedTitleValue(value);
  };

  const handleChangeTitle = () => {
    const { onChange } = props;

    // send event to google analytics
    ReactGA.event({
      category: "Postavka oglasa",
      action: "Click",
      label: "Primjena naslova oglasa"
    });

    // set title to suggested title
    onChange({ [field]: suggestedTitleValue });

    // disable input and button
    setDisabled(true);

    // set changed to true so we cant change title again
    setChanged(true);

    // show notification
    notification.show("Uspješno ste primijenili sugerirani naslov");
  };

  return hidden ? null : (<Row>
    <Col>
      <span className={styles.title}>
        {title}
      </span>
    </Col>
    <Col span={isMobile ? 24 : 12}>
      <Row>
        <Col>
          {!isMobile ? (
            <Input
              size={size}
              value={suggestedTitleValue}
              placeholder={placeholder}
              onChange={handleChange}
              disabled={isDisabled || disabled}
              className={styles.input}
              suffix={`${suggestedTitleValue.length} / 100`}
              style={{ resize: "none", height: "50px !important" }}
             
              />
          ) : (
            <React.Fragment>
              <AutoResizeTextArea
                value={suggestedTitleValue}
                placeholder={placeholder}
                onChange={handleChange}
                disabled={isDisabled || disabled}
                autoSize={{ minRows: 1, maxRows: 3 }}
                className={styles.mobileInput}
                />
                <span className={styles.mobileSpan}>
                  {`${suggestedTitleValue.length} / 100`}
                </span>
             </React.Fragment>
          )}
        </Col>
      </Row>
    </Col>
    <Col span={isMobile ? 24 : 4}>
      <Button
        type="primary"
        onClick={handleChangeTitle}
        className={styles.button}
        disabled={disabled}
      >
        {"Primjeni"}
      </Button>
    </Col>
  </Row>);
};

export default SuggestedTitle;



// WEBPACK FOOTER //
// ./src/components/FormControls/SuggestedTitle/index.tsx